import React, { useState } from 'react'
import * as XLSX from 'xlsx'
import { Card, Button, Input, Select, Table, Td, Badge, StatusBadge, EmptyState, Notification } from './ui'
import { AGENTS, TELESALES_AGENTS, MONTHLY_TARGETS, DATABASE_CONTACTS, CONTACT_STATUSES, CALL_OUTCOMES, LEAD_STATUSES, AM_LIST, DB_SOURCES, CALL_PURPOSES } from '../lib/constants'
import { evaluateCandidate, buildDuplicateAlert, normDomain } from '../lib/duplicateCheck'
import { exportMonthly, exportDatabase } from '../lib/export'
import { Check, Trash2, Plus, Upload, Phone, Pencil, Eye, ArrowLeftRight } from 'lucide-react'

// ─── REMINDERS ──────────────────────────────────────────────────────────────

const emptyRem = {
  agent: 'Halimah', prospect_name: '', company: '',
  follow_up_date: new Date().toISOString().slice(0, 10),
  follow_up_time: '09:00', priority: 'medium', notes: '', done: false,
}

export function Reminders({ reminders, addReminder, markReminderDone, deleteReminder }) {
  const [form, setForm] = useState(emptyRem)
  const [saving, setSaving] = useState(false)
  const [notif, setNotif] = useState('')
  const [filterAgent, setFilterAgent] = useState('')
  const [filterPrio, setFilterPrio] = useState('')
  const today = new Date().toISOString().slice(0, 10)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async () => {
    if (!form.prospect_name) { setNotif('Prospect name is required.'); return }
    setSaving(true)
    try { await addReminder(form); setNotif('Reminder saved!'); setForm(emptyRem) }
    catch (e) { setNotif('Error: ' + e.message) }
    finally { setSaving(false) }
  }

  const filtered = reminders
    .filter(r => (!filterAgent || r.agent === filterAgent) && (!filterPrio || r.priority === filterPrio))
    .sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date))

  const overdue = reminders.filter(r => !r.done && r.follow_up_date <= today).length
  const prioColor = { high: 'danger', medium: 'warning', low: 'success' }
  const prioLabel = { high: 'High', medium: 'Medium', low: 'Low' }
  const dotColor  = { high: '#E24B4A', medium: '#EF9F27', low: '#639922' }

  return (
    <div>
      <Notification message={notif} onClose={() => setNotif('')} />
      {overdue > 0 && (
        <div style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, padding: '9px 14px', fontSize: 12, marginBottom: '0.85rem' }}>
          {overdue} reminder{overdue > 1 ? 's' : ''} overdue or due today!
        </div>
      )}
      <Card>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: '.85rem' }}>Add Follow-up Reminder</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.85rem' }}>
          <Select label="Agent" value={form.agent} onChange={e => set('agent', e.target.value)}>{AGENTS.map(a => <option key={a}>{a}</option>)}</Select>
          <Input label="Prospect Name" value={form.prospect_name} placeholder="Contact name" onChange={e => set('prospect_name', e.target.value)} />
          <Input label="Company" value={form.company} placeholder="PT ..." onChange={e => set('company', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.85rem' }}>
          <Input label="Follow-up Date" type="date" value={form.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} />
          <Input label="Time" type="time" value={form.follow_up_time} onChange={e => set('follow_up_time', e.target.value)} />
          <Select label="Priority" value={form.priority} onChange={e => set('priority', e.target.value)}>
            <option value="high">High — Qualified</option>
            <option value="medium">Medium — Engaged</option>
            <option value="low">Low — Contacted</option>
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.85rem' }}>
          <Input label="Notes" value={form.notes} placeholder="What to discuss..." onChange={e => set('notes', e.target.value)} style={{ flex: 3 }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : '+ Save Reminder'}</Button>
        </div>
      </Card>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em' }}>All Reminders</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)} style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All agents</option>{AGENTS.map(a => <option key={a}>{a}</option>)}
            </select>
            <select value={filterPrio} onChange={e => setFilterPrio(e.target.value)} style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All priorities</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
        </div>
        {filtered.length === 0 ? <EmptyState>No reminders yet.</EmptyState> : filtered.map(r => {
          const isOverdue = !r.done && r.follow_up_date < today
          const isToday   = !r.done && r.follow_up_date === today
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', opacity: r.done ? 0.45 : 1 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: r.done ? '#639922' : (dotColor[r.priority] || '#888'), marginTop: 4, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{r.prospect_name}</span>
                  <span style={{ fontSize: 11, color: '#888' }}>{r.company}</span>
                  <Badge variant={prioColor[r.priority] || 'gray'}>{prioLabel[r.priority] || r.priority}</Badge>
                  {isOverdue && <Badge variant="danger">Overdue!</Badge>}
                  {isToday && <Badge variant="warning">Today</Badge>}
                  {r.done && <Badge variant="success">Done</Badge>}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{r.agent} · {r.follow_up_date} {r.follow_up_time} · {r.notes}</div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                {!r.done && (
                  <button onClick={() => markReminderDone(r.id)} style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#3B6D11' }}>
                    <Check size={12} /> Done
                  </button>
                )}
                <button onClick={() => deleteReminder(r.id)} style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#999' }}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          )
        })}
      </Card>
    </div>
  )
}

// ─── DISTRIBUTION ────────────────────────────────────────────────────────────

export function Distribution({ callLogs }) {
  const leads = callLogs.filter(r => r.passed_to_am)
  const amCounts = {}
  leads.forEach(r => { amCounts[r.passed_to_am] = (amCounts[r.passed_to_am] || 0) + 1 })
  const total = leads.length || 1
  const agentLeads = TELESALES_AGENTS.map(a => ({ agent: a, count: leads.filter(r => r.agent === a).length }))

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <Card>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>Leads Distribution to AM</div>
          {Object.entries(amCounts).length === 0 ? <EmptyState>No leads passed to AM yet.</EmptyState>
            : Object.entries(amCounts).sort((a, b) => b[1] - a[1]).map(([am, n]) => (
              <div key={am} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', fontSize: 12 }}>
                <span>{am}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 10, flex: 1 }}>
                  <div style={{ flex: 1, height: 5, background: 'rgba(0,0,0,0.07)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.round(n / total * 100)}%`, height: '100%', background: '#185FA5', borderRadius: 99 }} />
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 8 }}>{n} <span style={{ color: '#888' }}>({Math.round(n / total * 100)}%)</span></span>
              </div>
            ))}
        </Card>
        <Card>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>Leads Passed per Agent</div>
          {agentLeads.map(({ agent, count }) => (
            <div key={agent} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', fontSize: 12 }}>
              <span style={{ minWidth: 70 }}>{agent}</span>
              <div style={{ flex: 1, height: 5, background: 'rgba(0,0,0,0.07)', borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: leads.length > 0 ? `${Math.round(count / leads.length * 100)}%` : '0%', height: '100%', background: '#1D9E75', borderRadius: 99 }} />
              </div>
              <span style={{ fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{count}</span>
            </div>
          ))}
        </Card>
      </div>
      <Card>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>Leads Detail</div>
        <Table headers={['Agent', 'Prospect', 'Company', 'AM', 'Status', 'Date']}>
          {leads.length === 0 ? (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>No data yet.</td></tr>
          ) : leads.slice(0, 50).map(r => (
            <tr key={r.id}>
              <Td>{r.agent}</Td><Td style={{ fontWeight: 600 }}>{r.prospect_name}</Td>
              <Td>{r.company}</Td><Td style={{ fontWeight: 600 }}>{r.passed_to_am}</Td>
              <Td><StatusBadge status={r.lead_status} /></Td><Td>{r.call_date}</Td>
            </tr>
          ))}
        </Table>
      </Card>
    </div>
  )
}

// ─── MONTHLY ─────────────────────────────────────────────────────────────────

function ProgressRow({ label, value, target }) {
  const pct = target > 0 ? Math.min(100, Math.round(value / target * 100)) : 0
  const col = pct >= 100 ? '#1D9E75' : pct >= 60 ? '#EF9F27' : '#E24B4A'
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', fontSize: 12 }}>
      <span style={{ color: '#888', minWidth: 120 }}>{label}</span>
      <div style={{ flex: 1, height: 6, background: 'rgba(0,0,0,0.07)', borderRadius: 99, overflow: 'hidden', margin: '0 12px' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: col, borderRadius: 99 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, minWidth: 72, textAlign: 'right' }}>
        {value}/{target} <span style={{ fontSize: 10, color: col }}>{pct}%</span>
      </span>
    </div>
  )
}

export function Monthly({ callLogs }) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        {TELESALES_AGENTS.map(a => {
          const t = MONTHLY_TARGETS[a]
          const calls    = callLogs.filter(r => r.agent === a).length
          const meetings = callLogs.filter(r => r.agent === a && r.meeting_set === 'Yes').length
          const bant     = callLogs.filter(r => r.agent === a && r.bant_score >= 3).length
          return (
            <Card key={a}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 12 }}>Monthly KPI — {a}</div>
              <ProgressRow label="Outbound Calls" value={calls} target={t.calls} />
              <ProgressRow label="Meetings Set" value={meetings} target={t.meetings} />
              <ProgressRow label="BANT Leads (≥3)" value={bant} target={t.bant} />
            </Card>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <Button variant="success" size="sm" onClick={() => exportMonthly(callLogs, TELESALES_AGENTS, MONTHLY_TARGETS)}>Export Monthly Summary</Button>
      </div>
    </div>
  )
}

// ─── DATABASE ────────────────────────────────────────────────────────────────

const emptyContact = {
  company: '', industry: '', status: 'Not Yet Contacted', agent: 'Yonila',
  notes: '', source: 'Telesales Organic Search', event_name: '',
  contacts: [{ name: '', role: '', phone: '', email: '' }],
}

// Log a call directly from a contact
function LogCallModal({ contact, onClose, onSave }) {
  const today = new Date().toISOString().slice(0, 10)
  const firstContact = contact.contacts && contact.contacts[0] ? contact.contacts[0] : {}
  const [form, setForm] = useState({
    call_date: today, call_time: '09:00',
    agent: contact.agent || 'Yonila',
    prospect_name: contact.name || firstContact.name || '', title: contact.role || firstContact.role || '', company: contact.company,
    industry: contact.industry || '',
    outcome: CALL_OUTCOMES[0], bant_score: 0, lead_status: 'Contacted',
    meeting_set: 'No', passed_to_am: '', notes: '', follow_up_date: '', next_action: '',
    // tags: call purpose + lead origin (event name carried from the contact)
    call_purpose: '', event_name: contact.source === 'Event' ? (contact.event_name || '') : '',
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const [saving, setSaving] = useState(false)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Log Call — {contact.name || firstContact.name || contact.company}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Input label="Agent" value={form.agent} onChange={e => set('agent', e.target.value)} />
          <Input label="Date" type="date" value={form.call_date} onChange={e => set('call_date', e.target.value)} />
          <Input label="Time" type="time" value={form.call_time} onChange={e => set('call_time', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Select label="Tujuan call *" value={form.call_purpose} onChange={e => set('call_purpose', e.target.value)}>
            <option value="">— pilih —</option>
            {CALL_PURPOSES.map(p => <option key={p}>{p}</option>)}
          </Select>
          <Input label="Asal lead" value={form.event_name ? 'Event: ' + form.event_name : (contact.source || 'Telesales Organic Search')} disabled readOnly />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Select label="Outcome" value={form.outcome} onChange={e => set('outcome', e.target.value)}>
            {CALL_OUTCOMES.map(o => <option key={o}>{o}</option>)}
          </Select>
          <Select label="BANT (0-4)" value={form.bant_score} onChange={e => set('bant_score', parseInt(e.target.value))}>
            {[0,1,2,3,4].map(n => <option key={n} value={n}>{n}{n===4?' — Full':''}</option>)}
          </Select>
          <Select label="Lead Status" value={form.lead_status} onChange={e => set('lead_status', e.target.value)}>
            {LEAD_STATUSES.map(s => <option key={s}>{s}</option>)}
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Select label="Meeting Set?" value={form.meeting_set} onChange={e => set('meeting_set', e.target.value)}>
            <option>No</option><option>Yes</option>
          </Select>
          <Select label="Passed to AM" value={form.passed_to_am} onChange={e => set('passed_to_am', e.target.value)}>
            <option value="">— None —</option>
            {AM_LIST.map(a => <option key={a}>{a}</option>)}
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Input label="Notes" value={form.notes} placeholder="Call summary..." onChange={e => set('notes', e.target.value)} style={{ flex: 2 }} />
          <Input label="Follow-up Date" type="date" value={form.follow_up_date} onChange={e => set('follow_up_date', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={async () => {
            if (!form.call_purpose) { alert('Pilih tujuan call dulu.'); return }
            setSaving(true)
            try { await onSave(form); onClose() } catch(e) { alert('Error: ' + e.message) } finally { setSaving(false) }
          }}>
            {saving ? 'Saving...' : 'Save Call Log'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function EditContactModal({ contact, onClose, onSave, isSuperadmin = false }) {
  const [form, setForm] = useState({
    company: contact.company || '', industry: contact.industry || '',
    status: contact.status || 'Not Yet Contacted', agent: contact.agent || 'Yonila',
    notes: contact.notes || '', source: contact.source || 'Telesales Organic Search',
    event_name: contact.event_name || '',
    contacts: contact.contacts || [{ name: contact.name || '', role: contact.role || '', phone: contact.phone || '', email: contact.email || '' }],
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const setContact = (idx, k, v) => setForm(f => ({ ...f, contacts: f.contacts.map((c, i) => i === idx ? { ...c, [k]: v } : c) }))
  const addContact = () => setForm(f => ({ ...f, contacts: [...f.contacts, { name: '', role: '', phone: '', email: '' }] }))
  const removeContact = (idx) => setForm(f => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }))
  const [saving, setSaving] = useState(false)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Edit Company</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Input label="Company" value={form.company} placeholder="PT ..." onChange={e => set('company', e.target.value)} style={{ flex: 2 }} />
          <Input label="Industry" value={form.industry} placeholder="Manufaktur..." onChange={e => set('industry', e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
          <Select label="Status" value={form.status} onChange={e => set('status', e.target.value)}>
            {CONTACT_STATUSES.map(s => <option key={s}>{s}</option>)}
          </Select>
          <Select label="Agent" value={form.agent} onChange={e => set('agent', e.target.value)} disabled={!isSuperadmin} title={!isSuperadmin ? 'Hanya Dewa Admin yang bisa memindahkan kepemilikan' : undefined}>
            {TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
          </Select>
          <Select label="Source" value={form.source} onChange={e => set('source', e.target.value)}>
            {DB_SOURCES.map(s => <option key={s}>{s}</option>)}
          </Select>
        </div>
        {form.source === 'Event' && (
          <div style={{ marginBottom: '.75rem' }}>
            <Input label="Nama Event *" value={form.event_name} placeholder="Wajib diisi..." onChange={e => set('event_name', e.target.value)} />
          </div>
        )}
        <Input label="Notes" value={form.notes} placeholder="Additional notes..." onChange={e => set('notes', e.target.value)} style={{ marginBottom: '.75rem' }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8, marginTop: 8 }}>Contacts</div>
        {form.contacts.map((c, idx) => (
          <div key={idx} style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px', marginBottom: 6 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
              <Input label="Name" value={c.name} placeholder="Nama" onChange={e => setContact(idx, 'name', e.target.value)} />
              <Input label="Role" value={c.role} placeholder="Jabatan" onChange={e => setContact(idx, 'role', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Input label="Phone" value={c.phone} placeholder="+62..." onChange={e => setContact(idx, 'phone', e.target.value)} />
              <Input label="Email" value={c.email} placeholder="email@..." onChange={e => setContact(idx, 'email', e.target.value)} />
              {form.contacts.length > 1 && (
                <button onClick={() => removeContact(idx)} style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#999', fontSize: 11, marginBottom: 2 }}>✕</button>
              )}
            </div>
          </div>
        ))}
        <button onClick={addContact} style={{ background: 'none', border: '0.5px dashed rgba(0,0,0,0.25)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 11, color: '#666', width: '100%', marginBottom: '.75rem' }}>+ Add another contact</button>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={async () => {
            if (form.source === 'Event' && !form.event_name.trim()) { alert('Nama event wajib diisi.'); return }
            setSaving(true)
            try { await onSave(form); onClose() } catch(e) { alert('Error: ' + e.message) } finally { setSaving(false) }
          }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// Superadmin-only: reassign a db_contact from its current owner to another agent.
function TransferModal({ row, userName, onClose, onConfirm }) {
  const options = TELESALES_AGENTS.filter(a => a !== row.agent)
  const [toAgent, setToAgent] = useState(options[0] || '')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Transfer database</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: '#555', marginBottom: 14 }}>
          <strong>{row.company}</strong><br />
          Pemilik saat ini: <strong>{row.agent || '—'}</strong>
        </div>
        <Select label="Pindahkan ke agent" value={toAgent} onChange={e => setToAgent(e.target.value)}>
          {options.map(a => <option key={a}>{a}</option>)}
        </Select>
        <div style={{ marginTop: 10 }}>
          <Input label="Catatan (opsional)" value={note} placeholder="Alasan transfer..." onChange={e => setNote(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button onClick={onClose}>Batal</Button>
          <Button variant="primary" disabled={saving || !toAgent} onClick={async () => {
            setSaving(true)
            try { await onConfirm({ to_agent: toAgent, note }) } catch (e) { alert('Error: ' + e.message) } finally { setSaving(false) }
          }}>{saving ? 'Memindahkan...' : 'Transfer'}</Button>
        </div>
      </div>
    </div>
  )
}

function parseExcelRows(rows) {
  if (!rows || rows.length === 0) return []

  const keys = Object.keys(rows[0])
  const find = (...patterns) => keys.find(k => patterns.some(p => k.toLowerCase().includes(p)))

  // Detect if this is Odoo format (has "Company Type" column)
  const companyTypeKey = find('company type')
  const isOdooFormat = !!companyTypeKey

  if (isOdooFormat) {
    // Odoo format: Name*, Company Type*, Related Company, Email, Phone, Tags, Notes
    const nameKey = find('name')
    const relatedCompanyKey = find('related company')
    const emailKey = find('email')
    const phoneKey = find('phone')
    const tagsKey = find('tags')
    const notesKey = find('notes')
    const websiteKey = find('website')

    // First pass: collect companies and persons
    const companies = {} // name -> company data
    const persons = []   // {name, company, email, phone}

    for (const row of rows) {
      const type = String(row[companyTypeKey] || '').trim().toLowerCase()
      const name = String(row[nameKey] || '').trim()
      if (!name) continue

      if (type === 'company') {
        companies[name] = {
          company: name,
          industry: '',
          status: 'Not Yet Contacted',
          agent: 'Yonila',
          source: String(row[tagsKey] || 'Telesales Organic Search').trim(),
          notes: String(row[notesKey] || '').trim().replace(/<[^>]*>/g, ''),
          contacts: [],
        }
      } else {
        // Person
        const relatedCompany = String(row[relatedCompanyKey] || '').trim()
        persons.push({
          name,
          company: relatedCompany,
          email: String(row[emailKey] || '').trim(),
          phone: String(row[phoneKey] || '').trim(),
          role: '',
        })
      }
    }

    // Assign persons to their companies
    for (const p of persons) {
      if (p.company && companies[p.company]) {
        companies[p.company].contacts.push({ name: p.name, role: p.role, phone: p.phone, email: p.email })
      } else if (p.company) {
        // Company not explicitly listed — create it
        companies[p.company] = {
          company: p.company,
          industry: '',
          status: 'Not Yet Contacted',
          agent: 'Yonila',
          source: 'Telesales Organic Search',
          contacts: [{ name: p.name, role: p.role, phone: p.phone, email: p.email }],
        }
      } else {
        // Person without company — create standalone entry
        companies[p.name] = {
          company: p.name,
          industry: '',
          status: 'Not Yet Contacted',
          agent: 'Yonila',
          source: 'Telesales Organic Search',
          contacts: [{ name: p.name, role: '', phone: p.phone, email: p.email }],
        }
      }
    }

    return Object.values(companies)
  }

  // Standard format: Company, Industry, Status, Agent, Source, Name, Role, Phone, Email
  const keyMap = {}
  for (const k of keys) {
    const lk = k.toLowerCase()
    if (lk.includes('company') || lk.includes('perusahaan')) keyMap.company = k
    else if (lk.includes('industry') || lk.includes('industri')) keyMap.industry = k
    else if (lk.includes('status')) keyMap.status = k
    else if (lk.includes('agent')) keyMap.agent = k
    else if (lk.includes('source') || lk.includes('sumber')) keyMap.source = k
    else if (lk.includes('name') || lk.includes('nama')) keyMap.name = k
    else if (lk.includes('role') || lk.includes('jabatan') || lk.includes('title') || lk.includes('position')) keyMap.role = k
    else if (lk.includes('phone') || lk.includes('telepon') || lk.includes('no.') || lk.includes('hp')) keyMap.phone = k
    else if (lk.includes('email')) keyMap.email = k
  }

  const companyMap = {}
  for (const row of rows) {
    const company = String(row[keyMap.company] || '').trim()
    if (!company) continue
    if (!companyMap[company]) {
      companyMap[company] = {
        company,
        industry: String(row[keyMap.industry] || '').trim(),
        status: String(row[keyMap.status] || 'Not Yet Contacted').trim(),
        agent: String(row[keyMap.agent] || 'Yonila').trim(),
        source: String(row[keyMap.source] || 'Telesales Organic Search').trim(),
        contacts: [],
      }
    }
    const name = String(row[keyMap.name] || '').trim()
    const role = String(row[keyMap.role] || '').trim()
    const phone = String(row[keyMap.phone] || '').trim()
    const email = String(row[keyMap.email] || '').trim()
    if (name || role || phone || email) {
      companyMap[company].contacts.push({ name, role, phone, email })
    }
  }
  return Object.values(companyMap)
}

export function Database({ dbContacts, addDbContact, addDbContactsBulk, updateDbContact, deleteDbContact, addCallLog, isSuperadmin = false, userName = '', transferDbContact, checkDuplicate }) {
  const [search, setSearch]         = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterAgent, setFilterAgent]   = useState('')
  const [filterSource, setFilterSource] = useState('')
  const [filterIndustry, setFilterIndustry] = useState('')
  const [showForm, setShowForm]     = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [form, setForm]             = useState(emptyContact)
  const [saving, setSaving]         = useState(false)
  const [notif, setNotif]           = useState('')
  const [logCallContact, setLogCallContact] = useState(null)
  const [editContact, setEditContact] = useState(null)
  const [viewContact, setViewContact] = useState(null)
  const [duplicateConfirm, setDuplicateConfirm] = useState(null) // { pendingForm, matchedCompany }
  const [blockAlert, setBlockAlert] = useState(null) // { message } — duplicate owned by another agent
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 30

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const [transferRow, setTransferRow] = useState(null) // db_contact being transferred (superadmin)
  // Owner used for new rows + duplicate ownership checks. Agents are locked to
  // themselves; only superadmin may pick another agent.
  const currentAgent = isSuperadmin ? form.agent : (userName || form.agent)

  // The hardcoded seed also carries agent names, so it must be scoped too:
  // an agent only sees their own seed rows; superadmin sees all.
  const scopedSeed = isSuperadmin ? DATABASE_CONTACTS : DATABASE_CONTACTS.filter(c => c.agent === userName)
  const allContacts = [...scopedSeed, ...dbContacts]
  // Deduplicate: if a company exists in both hardcoded and Supabase (dbContacts), show only the Supabase version
  const dbCompanyNames = new Set(dbContacts.map(c => c.company?.trim().toLowerCase()).filter(Boolean))
  let deduped = allContacts.filter(r => {
    // If it's from hardcoded (no id) and a Supabase entry exists with same company name, skip it
    if (!r.id && r.company && dbCompanyNames.has(r.company.trim().toLowerCase())) return false
    return true
  })
  // Safety net: enforce per-agent visibility even if upstream scoping changes.
  if (!isSuperadmin) deduped = deduped.filter(r => (r.agent || '') === userName)
  const industries = [...new Set(deduped.map(r => r.industry).filter(Boolean))].sort()
  const filtered = deduped.filter(r =>
    (!search || (r.company + (r.contacts ? r.contacts.map(c => c.name).join(' ') : r.name || '') + r.industry).toLowerCase().includes(search.toLowerCase())) &&
    (!filterStatus || r.status === filterStatus) &&
    (!filterAgent || r.agent === filterAgent) &&
    (!filterSource || (r.source || 'Telesales Organic Search') === filterSource) &&
    (!filterIndustry || r.industry === filterIndustry)
  )
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const statusCount = s => deduped.filter(r => r.status === s).length

  const handleAdd = async () => {
    if (!form.company) { setNotif('Company name is required.'); return }
    if (form.source === 'Event' && !form.event_name.trim()) { setNotif('Nama event wajib diisi.'); return }

    // The new row is always owned by the acting agent (superadmin may pick).
    const candidate = { ...form, agent: currentAgent, created_by_agent: form.created_by_agent || currentAgent }

    if (!duplicateConfirm) {
      // 1) Authoritative cross-agent check via server RPC (works even though RLS
      //    hides other agents' rows from this client). Returns null if the RPC
      //    isn't deployed yet → we then fall back to the client-side check.
      let serverDup = null
      try { serverDup = await checkDuplicate && checkDuplicate({ company: form.company, domain: normDomain(form.company_domain || form.domain) }) } catch (e) { serverDup = null }
      if (serverDup && serverDup.exists) {
        const owner = serverDup.created_by_agent || serverDup.owner_agent || ''
        if (owner && owner !== currentAgent) {
          setBlockAlert({ message: buildDuplicateAlert({ created_by_agent: serverDup.created_by_agent, agent: serverDup.owner_agent }) })
          return
        }
        setDuplicateConfirm({ pendingForm: candidate, matchedCompany: serverDup.company || form.company })
        return
      }
      // 2) Client-side fallback (within the rows visible to this user)
      const verdict = evaluateCandidate(candidate, deduped, currentAgent)
      if (verdict.action === 'block') { setBlockAlert({ message: verdict.alert }); return }
      if (verdict.action === 'associate' || verdict.action === 'own-duplicate') {
        setDuplicateConfirm({ pendingForm: candidate, matchedCompany: verdict.record.company }); return
      }
      // 'allow' -> fall through
    }
    setDuplicateConfirm(null)
    setSaving(true)
    try { await addDbContact(candidate); setNotif('Contact added!'); setForm(emptyContact); setShowForm(false) }
    catch (e) { setNotif('Error: ' + e.message) }
    finally { setSaving(false) }
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = async (ev) => {
      try {
        const data = new Uint8Array(ev.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName])
        const companies = parseExcelRows(rows)
        if (companies.length === 0) { setNotif('No data found. Check file format.'); return }
        await addDbContactsBulk(companies)
        setNotif(`Successfully imported ${companies.length} companies!`)
        setShowImport(false)
      } catch(err) { setNotif('Error: ' + err.message) }
      e.target.value = ''
    }
    reader.readAsArrayBuffer(file)
  }

  return (
    <div>
      {logCallContact && (
        <LogCallModal
          contact={logCallContact}
          onClose={() => setLogCallContact(null)}
          onSave={async (callForm) => { await addCallLog(callForm); setNotif('Call logged successfully!') }}
        />
      )}
      {editContact && (
        <EditContactModal
          contact={editContact}
          isSuperadmin={isSuperadmin}
          onClose={() => setEditContact(null)}
          onSave={async (updates) => {
            if (editContact.id) {
              await updateDbContact(editContact.id, updates)
            } else {
              await addDbContact({ ...editContact, ...updates })
            }
            setNotif('Contact updated!')
          }}
        />
      )}

      {transferRow && (
        <TransferModal row={transferRow} userName={userName} onClose={() => setTransferRow(null)}
          onConfirm={async ({ to_agent, note }) => {
            await transferDbContact(transferRow.id, { from_agent: transferRow.agent, to_agent, transferred_by: userName, note })
            setNotif(`Database "${transferRow.company}" dipindahkan ke ${to_agent}.`)
            setTransferRow(null)
          }} />
      )}

      {blockAlert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 210, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 460, boxShadow: '0 4px 24px rgba(0,0,0,0.15)', borderTop: '4px solid #E24B4A' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#A32D2D', marginBottom: '0.75rem' }}>⚠️ Database sudah ada</div>
            <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6, marginBottom: '1.25rem', background: '#FCEBEB', borderRadius: 8, padding: '12px 14px' }}>
              {blockAlert.message}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="primary" onClick={() => setBlockAlert(null)}>Mengerti</Button>
            </div>
          </div>
        </div>
      )}

      {duplicateConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: '0.75rem' }}>Perusahaan sudah ada di database</div>
            <div style={{ fontSize: 13, color: '#555', marginBottom: '1.25rem' }}>
              <strong>"{duplicateConfirm.matchedCompany}"</strong> sudah ada di database.<br />
              Are you sure you want to put the same database?
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => setDuplicateConfirm(null)}>No</Button>
              <Button variant="primary" onClick={async () => {
                setSaving(true)
                try {
                  await addDbContact(duplicateConfirm.pendingForm)
                  setNotif('Contact added!')
                  setForm(emptyContact)
                  setShowForm(false)
                } catch(e) { setNotif('Error: ' + e.message) }
                finally { setSaving(false); setDuplicateConfirm(null) }
              }}>Yes, tambahkan</Button>
            </div>
          </div>
        </div>
      )}

      {viewContact && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{viewContact.company}</div>
              <button onClick={() => setViewContact(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
            </div>
            {[
              { label: 'Industry', value: viewContact.industry },
              { label: 'Status', value: viewContact.status },
              { label: 'Agent', value: viewContact.agent },
              { label: 'Source', value: viewContact.source || 'Telesales Organic Search' },
              { label: 'Event Name', value: viewContact.event_name },
              { label: 'Notes', value: viewContact.notes },
            ].filter(f => f.value).map(f => (
              <div key={f.label} style={{ display: 'flex', padding: '6px 0', borderBottom: '0.5px solid rgba(0,0,0,0.07)', fontSize: 13 }}>
                <span style={{ minWidth: 100, color: '#888', fontWeight: 500 }}>{f.label}</span>
                <span style={{ color: '#222' }}>{f.value}</span>
              </div>
            ))}
            {viewContact.contacts && viewContact.contacts.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>
                  Contacts ({viewContact.contacts.length})
                </div>
                {viewContact.contacts.map((c, idx) => (
                  <div key={idx} style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px 12px', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name || '-'}</div>
                    {c.role && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{c.role}</div>}
                    <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12 }}>
                      {c.phone && <span style={{ color: '#185FA5' }}>{c.phone}</span>}
                      {c.email && <span style={{ color: '#1D9E75' }}>{c.email}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Fallback for old format contacts without contacts array */}
            {!viewContact.contacts && viewContact.name && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Contact</div>
                <div style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{viewContact.name}</div>
                  {viewContact.role && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{viewContact.role}</div>}
                  <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12 }}>
                    {viewContact.phone && <span style={{ color: '#185FA5' }}>{viewContact.phone}</span>}
                    {viewContact.email && <span style={{ color: '#1D9E75' }}>{viewContact.email}</span>}
                  </div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <Button onClick={() => { setEditContact(viewContact); setViewContact(null) }}>Edit</Button>
              <Button variant="primary" onClick={() => { setLogCallContact(viewContact); setViewContact(null) }}>Log Call</Button>
            </div>
          </div>
        </div>
      )}

      <Notification message={notif} onClose={() => setNotif('')} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
        {[
          { label: 'Total contacts', value: deduped.length },
          { label: 'Contacted', value: statusCount('Contacted') },
          { label: 'Follow up later', value: statusCount('Follow Up Later') },
          { label: 'Not interested', value: statusCount('Not Interested') },
          { label: 'Not yet contacted', value: statusCount('Not Yet Contacted') },
        ].map(m => (
          <div key={m.label} style={{ background: '#f9f9f7', borderRadius: 8, padding: '.85rem', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 5 }}>{m.label}</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{m.value}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <Card>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: '.85rem' }}>Add New Company</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
            <Input label="Company" value={form.company} placeholder="PT ..." onChange={e => setF('company', e.target.value)} style={{ flex: 2 }} />
            <Input label="Industry" value={form.industry} placeholder="Manufaktur, Fintech..." onChange={e => setF('industry', e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '.75rem' }}>
            <Select label="Status" value={form.status} onChange={e => setF('status', e.target.value)}>
              {CONTACT_STATUSES.map(s => <option key={s}>{s}</option>)}
            </Select>
            {isSuperadmin ? (
              <Select label="Agent" value={form.agent} onChange={e => setF('agent', e.target.value)}>
                {TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
              </Select>
            ) : (
              <Select label="Agent" value={userName} disabled title="Database baru otomatis milik Anda">
                <option>{userName}</option>
              </Select>
            )}
            <Select label="Source" value={form.source} onChange={e => setF('source', e.target.value)}>
              {DB_SOURCES.map(s => <option key={s}>{s}</option>)}
            </Select>
          </div>
          {form.source === 'Event' && (
            <div style={{ marginBottom: '.75rem' }}>
              <Input label="Nama Event *" value={form.event_name} placeholder="Wajib diisi..." onChange={e => setF('event_name', e.target.value)} />
            </div>
          )}
          <Input label="Notes" value={form.notes} placeholder="Additional notes..." onChange={e => setF('notes', e.target.value)} style={{ marginBottom: '.75rem' }} />
          <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Contacts</div>
          {(form.contacts || []).map((c, idx) => (
            <div key={idx} style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px', marginBottom: 6 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                <Input label="Name" value={c.name} placeholder="Nama kontak" onChange={e => { const cs = [...form.contacts]; cs[idx] = {...cs[idx], name: e.target.value}; setF('contacts', cs) }} />
                <Input label="Role" value={c.role} placeholder="Jabatan" onChange={e => { const cs = [...form.contacts]; cs[idx] = {...cs[idx], role: e.target.value}; setF('contacts', cs) }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <Input label="Phone" value={c.phone} placeholder="+62..." onChange={e => { const cs = [...form.contacts]; cs[idx] = {...cs[idx], phone: e.target.value}; setF('contacts', cs) }} />
                <Input label="Email" value={c.email} placeholder="email@..." onChange={e => { const cs = [...form.contacts]; cs[idx] = {...cs[idx], email: e.target.value}; setF('contacts', cs) }} />
                {form.contacts.length > 1 && (
                  <button onClick={() => setF('contacts', form.contacts.filter((_, i) => i !== idx))} style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#999', fontSize: 11, marginBottom: 2 }}>✕</button>
                )}
              </div>
            </div>
          ))}
          <button onClick={() => setF('contacts', [...(form.contacts || []), { name: '', role: '', phone: '', email: '' }])} style={{ background: 'none', border: '0.5px dashed rgba(0,0,0,0.25)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 11, color: '#666', width: '100%', marginBottom: '.75rem' }}>+ Add another contact</button>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={() => setShowForm(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Save Company'}</Button>
          </div>
        </Card>
      )}

      {showImport && (
        <Card>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Import Contacts from CSV</div>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Upload file Excel (.xlsx) langsung.<br />
            Kolom: <code>Company, Industry, Status, Agent, Source, Name, Role, Phone, Email</code><br />
            Perusahaan yang sama akan otomatis di-merge jadi 1 entry dengan multiple contacts.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload}
              style={{ fontSize: 12, padding: '7px 9px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, flex: 1 }} />
            <Button onClick={() => setShowImport(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            Contact Database ({filtered.length})
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} placeholder="Search..."
              style={{ fontSize: 12, padding: '5px 9px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff', width: 160 }} />
            <select value={filterAgent} onChange={e => { setFilterAgent(e.target.value); setPage(1) }}
              style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All agents</option>{TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
            </select>
            <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
              style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All statuses</option>{CONTACT_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1) }}
              style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All sources</option>{DB_SOURCES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select value={filterIndustry} onChange={e => { setFilterIndustry(e.target.value); setPage(1) }}
              style={{ fontSize: 11, padding: '4px 7px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: '#fff' }}>
              <option value="">All industries</option>{industries.map(i => <option key={i}>{i}</option>)}
            </select>
            <Button size="sm" onClick={() => { setShowForm(v => !v); setShowImport(false) }}>
              <Plus size={12} /> Add
            </Button>
            <Button size="sm" onClick={() => { setShowImport(v => !v); setShowForm(false) }}>
              <Upload size={12} /> Import Excel
            </Button>
            <Button size="sm" variant="success" onClick={() => exportDatabase(filtered)}>Export</Button>
          </div>
        </div>
        <Table headers={['Company', 'Industry', 'Contacts', 'Status', 'Agent', 'Source', 'Actions']}>
          {filtered.length === 0 ? (
            <tr><td colSpan={7} style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>No contacts found.</td></tr>
          ) : paginated.map((r, i) => (
            <tr key={r.id || i}>
              <Td style={{ fontWeight: 600 }}>{r.company}</Td>
              <Td style={{ fontSize: 11 }}>{r.industry}</Td>
              <Td style={{ fontSize: 11 }}>
                {r.contacts
                  ? <span>{r.contacts.length} contact{r.contacts.length > 1 ? 's' : ''}</span>
                  : <span>{r.name || '-'}</span>}
              </Td>
              <Td><StatusBadge status={r.status} /></Td>
              <Td style={{ fontSize: 11 }}>{r.agent}</Td>
              <Td style={{ fontSize: 11 }}>
                {r.source === 'Event' && r.event_name
                  ? <span>Event: <strong>{r.event_name}</strong></span>
                  : (r.source || 'Telesales Organic Search')}
              </Td>
              <Td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => setViewContact(r)}
                    title="View details"
                    style={{ background: '#F0FAF5', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#1D9E75', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                    <Eye size={11} /> View
                  </button>
                  <button
                    onClick={() => setLogCallContact(r)}
                    title="Log a call for this contact"
                    style={{ background: '#E6F1FB', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#185FA5', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                    <Phone size={11} /> Call
                  </button>
                  <button
                    onClick={() => setEditContact(r)}
                    title="Edit contact"
                    style={{ background: '#F0F0F0', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#444', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                    <Pencil size={11} /> Edit
                  </button>
                  {isSuperadmin && r.id && transferDbContact && (
                    <button
                      onClick={() => setTransferRow(r)}
                      title="Transfer kepemilikan ke agent lain"
                      style={{ background: '#FBEFF4', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#993556', display: 'flex', alignItems: 'center', gap: 3, fontSize: 11 }}>
                      <ArrowLeftRight size={11} /> Transfer
                    </button>
                  )}
                  {r.id && (
                    <button onClick={() => deleteDbContact(r.id)}
                      style={{ background: 'none', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: '#999' }}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', fontSize: 12, color: '#888' }}>
          <span>{filtered.length} contacts total — page {page} of {totalPages}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}
              style={{ padding: '4px 12px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: page === 1 ? '#f5f5f3' : '#fff', cursor: page === 1 ? 'default' : 'pointer', fontSize: 12, color: '#666' }}>
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i+1).filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1).map((p, idx, arr) => (
              <React.Fragment key={p}>
                {idx > 0 && arr[idx-1] !== p-1 && <span style={{ padding: '4px 2px', color: '#ccc' }}>…</span>}
                <button onClick={() => setPage(p)}
                  style={{ padding: '4px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: p === page ? '#185FA5' : '#fff', color: p === page ? '#fff' : '#666', cursor: 'pointer', fontSize: 12, fontWeight: p === page ? 600 : 400 }}>
                  {p}
                </button>
              </React.Fragment>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}
              style={{ padding: '4px 12px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 6, background: page === totalPages ? '#f5f5f3' : '#fff', cursor: page === totalPages ? 'default' : 'pointer', fontSize: 12, color: '#666' }}>
              Next
            </button>
          </div>
        </div>
      </Card>
    </div>
  )
}
